// For Lovable (current)
import { supabase } from '@/integrations/supabase/client'
import { aiCreditsService } from '@/services/ai-credits-service'
// For local development with env variables (commented out)
// import { supabase } from './supabase'

import { getCurrentTenantSlug } from '@/utils/tenant-utils'

// Get current workspace ID from URL or context
const getCurrentWorkspaceId = async (): Promise<string | null> => {
  try {
    // 1) Try to detect current workspace by slug in the URL (works in staging/prod subdomains and dev ?tenant=)
    const tenantSlug = getCurrentTenantSlug();
    if (tenantSlug && tenantSlug !== 'default') {
      const { data: workspace } = await (supabase as any)
        .from('watt_workspaces')
        .select('id')
        .eq('slug', tenantSlug)
        .single();
      if (workspace?.id) return workspace.id;
    }

    // Get user's current/default workspace
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    // First try to get the default workspace
    let { data: membership } = await (supabase as any)
      .from('workspace_memberships')
      .select('workspace_id')
      .eq('user_id', user.id)
      .eq('status', 'active')
      .eq('is_default', true)
      .single();

    // If no default workspace, get the first active workspace
    if (!membership) {
      const { data: memberships } = await (supabase as any)
        .from('workspace_memberships')
        .select('workspace_id')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .order('created_at', { ascending: true })
        .limit(1)
        .single();
      
      membership = memberships;
    }

    return membership?.workspace_id || null;
  } catch (error) {
    console.error('Error getting current workspace ID:', error);
    return null;
  }
}

// Function to get all users for problem owner selection - V3 project-aware
export const getProblemOwners = async (projectId?: string) => {
  try {
    // If no projectId provided, get the first project the user is a member of
    let currentProjectId = projectId;
    if (!currentProjectId) {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data: projectMembership } = await (supabase as any)
        .from('project_members')
        .select('project_id')
        .eq('user_id', user.id)
        .limit(1)
        .single();

      currentProjectId = projectMembership?.project_id;
      if (!currentProjectId) return [];
    }

    // Query project_members and join watt_users
    const { data, error } = await (supabase as any)
      .from('project_members')
      .select(`
        user_id,
        role,
        watt_users!project_members_user_id_fkey (
          id,
          display_name,
          email
        )
      `)
      .eq('project_id', currentProjectId)
      .order('role', { ascending: true });

    if (error) throw error;

    const result = data?.map(pm => ({
      id: pm.watt_users?.id,
      display_name: pm.watt_users?.display_name,
      email: pm.watt_users?.email,
      role: pm.role
    })) || [];

    return result;
  } catch (error) {
    console.error('Error fetching problem owners:', error)
    return []
  }
}

export const chatService = {
  // User operations - V3 workspace-aware
  async getCurrentUser() {
    // Real authentication code for V3
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return null
    
    const { data } = await (supabase as any)
      .from('watt_users')
      .select(`
        *,
        workspace_memberships!workspace_memberships_user_id_fkey (
          role,
          workspace_id,
          status,
          is_default
        )
      `)
      .eq('id', user.id)
      .single()
    
    return data
  },

  async updateUserProfile(displayName: string) {
    // Real authentication code for V3
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('No authenticated user')

    const { data, error } = await supabase
      .from('watt_users')
      .upsert({
        id: user.id,
        email: user.email,
        display_name: displayName
      })
      .select()
      .single()

    if (error) throw error
    return data
  },

  // Channel member operations - V3 project-aware
  async addChannelMember(channelId: string, userInfo: any) {
    try {
      // Get the project_id from the channel
      const { data: channel, error: channelError } = await (supabase as any)
        .from('watt_channels')
        .select('project_id, workspace_id')
        .eq('id', channelId)
        .single();

      if (channelError || !channel?.project_id) {
        throw new Error('Could not find channel or project_id');
      }

      // Extract user ID and info
      const userId = typeof userInfo === 'string' ? userInfo : userInfo.id
      const userData = typeof userInfo === 'object' ? userInfo : null

      // Ensure user exists in watt_users
      const { data: existingUser } = await supabase
        .from('watt_users')
        .select('id')
        .eq('id', userId)
        .single()

      if (!existingUser && userData) {
        // Insert user if not present
        await supabase
          .from('watt_users')
          .insert({
            id: userData.id,
            email: userData.email || '',
            display_name: userData.display_name || userData.name || ''
          })
      }

      // Verify user is a member of the project
      const { data: projectMember, error: projectError } = await (supabase as any)
        .from('project_members')
        .select('id')
        .eq('project_id', channel.project_id)
        .eq('user_id', userId)
        .single();

      if (projectError || !projectMember) {
        throw new Error('User is not a member of this project');
      }

      // Now add the channel member to watt_members table
      const { data, error } = await (supabase as any)
        .from('watt_members')
        .insert([{
          channel_id: channelId,
          user_id: userId,
          workspace_id: channel.workspace_id // Add workspace_id from the channel
        }])
        .select()
        .single()

      if (error) {
        console.error('Error inserting into watt_members:', error)
        throw error
      }

      return data
    } catch (error) {
      console.error('Error adding channel member:', error)
      throw error
    }
  },

  // Channel operations - V3 workspace-aware
  async createChannel(channelData: any) {
    try {
      // Debug: capture current user id for visibility
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      
      // Get workspace_id from the project
      const { data: projectData, error: projectError } = await (supabase as any)
        .from('products')
        .select('workspace_id')
        .eq('id', channelData.project_id)
        .single()

      if (projectError) {
        console.error('Error fetching workspace_id:', projectError)
        throw projectError
      }

      const finalData = {
        ...channelData,
        workspace_id: projectData.workspace_id
      }

      // Debug: log identifiers used for RLS checks
      try { /* debug logs removed */ } catch {}

      // Create the channel
      const { data: channel, error } = await (supabase as any)
        .from('watt_channels')
        .insert([finalData])
        .select()
        .single()

      if (error) {
        console.error('chatService: Error creating channel:', error)
        throw error
      }

      // Automatically add the creator to the channel as a member
      try {
        await this.addChannelMember(channel.id, channelData.created_by)
      } catch (memberError) {
        console.error('chatService: Error adding creator to channel:', memberError)
        // Don't throw here - channel was created successfully
      }

      return channel
    } catch (error) {
      console.error('chatService: Error creating channel:', error)
      throw error
    }
  },

  async getChannels() {
    try {
      // Filter by the user's current workspace to avoid cross-workspace leakage in the UI
      const workspaceId = await getCurrentWorkspaceId();
      if (!workspaceId) {
        return [];
      }

      // Since RLS allows channels by membership, we still filter by membership client-side
      const { data, error } = await (supabase as any)
        .from('watt_channels')
        .select(`
          *,
          watt_members!watt_members_channel_id_fkey (
            user_id
          )
        `)
        .eq('workspace_id', workspaceId)
        .order('created_at', { ascending: true })
      
      if (error) throw error
      
      // Filter to only show channels where the current user is a member
      const currentUserId = (await supabase.auth.getUser()).data.user?.id;
      const filteredChannels = data?.filter((channel: any) => 
        channel.watt_members?.some((member: any) => member.user_id === currentUserId)
      ) || [];
      
      return filteredChannels;
    } catch (error) {
      console.error('Error getting channels:', error)
      return []
    }
  },

  // Message operations - V3 workspace-aware
  async getMessages(channelId: string) {
    try {
      // Get current workspace ID
      const workspaceId = await getCurrentWorkspaceId();
      if (!workspaceId) {
        return []; // Return empty array if no workspace context
      }

      const { data, error } = await (supabase as any)
        .from('watt_messages')
        .select(`
          *,
          watt_users!watt_messages_user_id_fkey (
            id,
            display_name,
            job_title,
            workspace_memberships!workspace_memberships_user_id_fkey (
              role,
              workspace_id,
              status
            )
          ),
          attachments:watt_attachments!watt_attachments_message_id_fkey(*)
        `)
        .eq('channel_id', channelId)
        .eq('workspace_id', workspaceId)
        .order('created_at', { ascending: true })
      
      if (error) throw error

      return data?.map(message => ({
        ...message,
        user: {
          ...message.watt_users,
          role: (message.watt_users?.workspace_memberships as any)?.[0]?.role || 'workspace_member'
        }
      })) || []
    } catch (error) {
      console.error('Error getting messages:', error)
      return []
    }
  },

  async sendMessage(channelId: string, content: string, attachments: any[] = [], isAI = false) {
    // Get current workspace ID
    const workspaceId = await getCurrentWorkspaceId();
    if (!workspaceId) {
      throw new Error('No workspace context available');
    }

    const userId = (await supabase.auth.getUser()).data.user?.id;
    if (!userId) throw new Error('No authenticated user')

    try {
      // First create the message
      const { data: message, error: messageError } = await (supabase as any)
        .from('watt_messages')
        .insert([{
          channel_id: channelId,
          content: content,
          user_id: userId,
          workspace_id: workspaceId
        }])
        .select(`
          *,
          watt_users:user_id (
            id,
            display_name,
            job_title,
            workspace_memberships!workspace_memberships_user_id_fkey (
              role,
              workspace_id,
              status
            )
          ),
          attachments:watt_attachments(*)
        `)
        .single()

      if (messageError) throw messageError

      // Then handle attachments if any
      const messageAttachments = []
      if (attachments && attachments.length > 0) {
        for (const file of attachments) {
          try {
            // Create attachment record using the pre-uploaded file info
                          const { data: attachment, error: attachmentError } = await (supabase as any)
                .from('watt_attachments')
                .insert([{
                  message_id: message.id,
                  file_name: file.name,
                  file_type: file.type,
                  file_size: file.size,
                  storage_path: file.path,
                  workspace_id: workspaceId,
                  uploaded_by: userId
                }])
                .select()
                .single()

            if (attachmentError) throw attachmentError
            messageAttachments.push(attachment)
          } catch (error) {
            console.error('Error processing attachment:', error)
            // Continue with other attachments even if one fails
          }
        }
      }

      // Return the complete message with user info and attachments
      return {
        ...message,
        user: {
          ...message.watt_users,
          role: (message.watt_users?.workspace_memberships as any)?.[0]?.role || 'workspace_member'
        },
        attachments: messageAttachments
      }
    } catch (error) {
      console.error('Error in sendMessage:', error)
      throw error
    }
  },

  async getAttachmentUrl(storagePath: string) {
    // Attempt signed URL first
    try {
      const { data, error } = await supabase.storage
        .from('watt-attachments')
        .createSignedUrl(storagePath, 60 * 5)
      if (!error && data?.signedUrl) return data.signedUrl
    } catch {}

    // Fallback to authenticated download → blob URL
    const { data: blob, error: dlError } = await supabase.storage
      .from('watt-attachments')
      .download(storagePath)
    if (dlError || !blob) throw dlError || new Error('Failed to fetch attachment')
    return URL.createObjectURL(blob)
  },

  // Get all attachments for a specific channel - V3 workspace-aware
  async getChannelAttachments(channelId: string) {
    try {
      // Get current workspace ID
      const workspaceId = await getCurrentWorkspaceId();
      if (!workspaceId) {
        return []; // Return empty array if no workspace context
      }

      const { data, error } = await (supabase as any)
        .from('watt_attachments')
        .select(`
          *,
          watt_messages:message_id (
            id,
            content,
            created_at,
            channel_id,
            watt_users:user_id (
              id,
              display_name,
              job_title,
              workspace_memberships!workspace_memberships_user_id_fkey (
                role,
                workspace_id,
                status
              )
            )
          )
        `)
        .eq('workspace_id', workspaceId)
        .order('created_at', { ascending: false })

      if (error) throw error
      
      // Filter attachments to only include those from the specified channel
      const channelAttachments = data?.filter(attachment => 
        attachment.watt_messages?.channel_id === channelId
      ) || []
      
      return channelAttachments
    } catch (error) {
      console.error('Error getting channel attachments:', error)
      return []
    }
  },

  // Real-time subscriptions - V3 workspace-aware
  subscribeToMessages(channelId: string, callback: (message: any) => void) {
    const channelName = `messages-${channelId}`
    
    const subscription = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'watt_messages'
        },
        async (payload) => {
          
          // Only process messages for this channel
          if (payload.new && payload.new.channel_id === channelId) {
            
            try {
              // Get complete message data including user info
              const { data: messageData, error } = await (supabase as any)
                .from('watt_messages')
                .select(`
                  *,
                  watt_users!watt_messages_user_id_fkey (
                    id,
                    display_name,
                    email
                  ),
                  attachments:watt_attachments!watt_attachments_message_id_fkey(*)
                `)
                .eq('id', payload.new.id)
                .single()

              if (error) {
                console.error(`[${channelName}] Error fetching message data:`, error)
                return
              }
              
              if (!messageData) {
                console.error(`[${channelName}] No message data found`)
                return
              }

              // Process the message
              const messageWithUser = {
                ...messageData,
                user: {
                  ...messageData.watt_users,
                  role: 'workspace_member' // Default role
                },
                attachments: messageData.attachments || []
              }

              callback(messageWithUser)
            } catch (error) {
              console.error(`[${channelName}] Error processing message:`, error)
            }
          }
        }
      )
      .subscribe((status) => {
        
      })

    return subscription
  },

  subscribeToChannels(callback: (channel: any) => void) {
    const subscription = supabase
      .channel('public:watt_channels')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'watt_channels'
      }, payload => {
        callback(payload.new)
      })
      .subscribe()

    return subscription
  },

  subscribeToChannelMembers(channelId: string, callback: (memberUpdate: any) => void) {
    const channelName = `members-${channelId}`
    
    const subscription = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'watt_members',
          filter: `channel_id=eq.${channelId}`
        },
        (payload) => {
          
          callback(payload)
        }
      )
      .subscribe((status) => {
        
      })

    return subscription
  },

  subscribeToWattMembers(callback: (memberUpdate: any) => void) {
    const channelName = 'watt-members-global'
    
    const subscription = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'watt_members'
        },
        (payload) => {
          
          callback(payload)
        }
      )
      .subscribe((status) => {
        
      })

    return subscription
  },

  subscribeToAllMessages(callback: (messageUpdate: any) => void) {
    const channelName = 'all-messages-global'
    
    const subscription = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'watt_messages'
        },
        (payload) => {
          
          callback(payload)
        }
      )
      .subscribe((status) => {
        
      })

    return subscription
  },

  async isUserChannelMember(channelId: string): Promise<boolean> {
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        return false
      }

      // Check if user is a member of this channel
      const { data, error } = await (supabase as any)
        .from('watt_members')
        .select('id')
        .eq('channel_id', channelId)
        .eq('user_id', user.id)
        .maybeSingle() // Use maybeSingle instead of single to avoid errors

      if (error) {
        console.error('Error checking channel membership:', error)
        return false
      }

      const isMember = !!data
      return isMember
    } catch (error) {
      console.error('Error checking channel membership:', error)
      return false
    }
  },

  // AI message processing - V3 workspace-aware with AI credits
  async processAIMessage(channelId: string, userMessage: string) {
    try {
      // Get current workspace ID
      const workspaceId = await getCurrentWorkspaceId();
      if (!workspaceId) {
        throw new Error('No workspace context available');
      }

      // Get current user ID
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('No authenticated user');
      }

      // Check if workspace has enough AI credits
      const hasEnoughCredits = await aiCreditsService.hasEnoughCredits(workspaceId, 1);
      if (!hasEnoughCredits) {
        // Send error message about insufficient credits
        const { data: errorMessage, error: errorMsgError } = await (supabase as any)
          .from('watt_messages')
          .insert([{
            channel_id: channelId,
            content: "❌ **Insufficient AI Credits**\n\nYou don't have enough AI credits to process this request. Please purchase additional credits or wait until your monthly allowance resets.\n\nYou can buy credits from your workspace dashboard.",
            user_id: 'watt-ai-assistant-001',
            workspace_id: workspaceId
          }])
          .select(`
            *,
            watt_users!watt_messages_user_id_fkey (
              id,
              display_name,
              job_title,
              workspace_memberships!workspace_memberships_user_id_fkey (
                role,
                workspace_id,
                status
              )
            )
          `)
          .single();

        if (errorMsgError) {
          console.error('Error sending credit error message:', errorMsgError);
          throw errorMsgError;
        }

        return {
          ...errorMessage,
          user: {
            ...errorMessage.watt_users,
            role: (errorMessage.watt_users?.workspace_memberships as any)?.[0]?.role || 'workspace_member'
          },
          attachments: []
        };
      }

      // Use AI credits for this operation
      const creditsUsed = await aiCreditsService.useCredits(
        workspaceId,
        user.id,
        1, // 1 credit for chat message
        'chat_message',
        {
          message_length: userMessage.length,
          channel_id: channelId,
          query: userMessage.replace(/^hey watt/i, '').trim()
        }
      );

      if (!creditsUsed) {
        throw new Error('Failed to use AI credits');
      }

      // Remove the trigger phrase and get the actual query
      const query = userMessage.replace(/^hey watt/i, '').trim()
      
      // Generate AI response based on the query
      let aiResponse: string
      if (!query) {
        aiResponse = "Hello! I'm Watt, your AI assistant. How can I help you today?"
      } else if (query.toLowerCase().includes('how are you')) {
        aiResponse = "I'm functioning perfectly! Thank you for asking. How can I assist you today?"
      } else {
        aiResponse = `I understand you said: "${query}". I'm still learning to process more complex queries. Could you try asking something specific about quality control or manufacturing processes?`
      }

      // Send AI response as a new message
      const { data: aiMessage, error: aiError } = await (supabase as any)
        .from('watt_messages')
        .insert([{
          channel_id: channelId,
          content: aiResponse,
          user_id: 'watt-ai-assistant-001', // Use a static AI user id
          workspace_id: workspaceId
        }])
        .select(`
          *,
          watt_users!watt_messages_user_id_fkey (
            id,
            display_name,
            job_title,
            workspace_memberships!workspace_memberships_user_id_fkey (
              role,
              workspace_id,
              status
            )
          )
        `)
        .single()

      if (aiError) {
        console.error('Error sending AI response:', aiError)
        throw aiError
      }

      // Return the complete message with user info
      return {
        ...aiMessage,
        user: {
          ...aiMessage.watt_users,
          role: (aiMessage.watt_users?.workspace_memberships as any)?.[0]?.role || 'workspace_member'
        },
        attachments: []
      }
    } catch (error) {
      console.error('Error processing AI message:', error)
      throw error
    }
  },

  async getChannelMembers(channelId: string) {
    try {
      // Get the project_id from the channel
      const { data: channel, error: channelError } = await (supabase as any)
        .from('watt_channels')
        .select('project_id')
        .eq('id', channelId)
        .single();

      if (channelError || !channel?.project_id) {
        return []; // Return empty array if no project context
      }

      const { data, error } = await (supabase as any)
        .from('watt_members')
        .select(`
          user_id,
          watt_users:user_id (
            id,
            display_name,
            job_title,
            email,
            project_members!project_members_user_id_fkey (
              role,
              project_id
            )
          )
        `)
        .eq('channel_id', channelId)

      if (error) throw error

      const result = data?.map(member => {
        // Filter project_members to only include the specific project
        const projectMembers = (member.watt_users?.project_members as any[]) || [];
        const projectMember = projectMembers.find(pm => pm.project_id === channel.project_id);
        
        return {
          ...member,
          user: {
            ...member.watt_users,
            role: projectMember?.role || 'project_member'
          }
        }
      }) || [];
      
      return result
    } catch (error) {
      console.error('Error getting channel members:', error)
      return []
    }
  }
} 